<template>
  <v-dialog
    :content-class="dialogDynamicClass"
    origin="top right"
    overlay-color="rgba(77, 92, 116, 0.25)"
    overlay-opacity="1"
    transition="slide-x-reverse-transition"
    v-model="isModalOpen"
    :persistent="persistent"
    @click:outside="onClose"
  >
    <div class="base-dialog__surface" role="dialog" aria-modal="true" :aria-labelledby="showHeader ? titleId : null">
      <div class="base-dialog__header" v-if="showHeader">
        <h2 :id="titleId" class="header__title">
          <slot name="title">
            {{ $t('dialog.title') }}
          </slot>
        </h2>
        <div
          ref="closeButton"
          class="close-icon__container"
          role="button"
          tabindex="0"
          :aria-label="$t('actions.close')"
          @click="closeModal"
          @keydown.enter.prevent="closeModal"
          @keydown.space.prevent="closeModal"
        >
          <slot name="close-icon">
            <BaseIcon name="x-circle" class="header__close-icon" />
          </slot>
        </div>
      </div>
      <div class="base-dialog__content">
        <slot name="content"> </slot>
      </div>
      <div class="base-dialog__actions" v-if="showActions">
        <slot name="actions"> </slot>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { VDialog } from 'vuetify/lib';
import BaseIcon from '@/components/base/BaseIcon';
import { closeOnBack } from '@/util/back-button';

export default {
  name: 'BaseDialog',
  components: {
    BaseIcon,
    VDialog,
  },
  props: {
    showHeader: {
      default: () => true,
      type: Boolean,
    },
    showActions: {
      default: () => false,
      type: Boolean,
    },
    contentClass: {
      default: () => 'normal',
      type: String,
    },
    persistent: {
      default: () => false,
      type: Boolean,
    },
    onClose: {
      default: () => null,
      type: Function,
    },
    sidePanel: {
      type: Boolean,
      default: false,
    },
    // Centered over the page (a confirmation, a short form) rather than a panel at its right edge.
    // Its own class still sizes it on larger screens; on phones it keeps 16px from the screen's
    // edges, where a panel fills the screen.
    centered: {
      type: Boolean,
      default: false,
    },
    // Give the content area a white background with standard padding, instead of
    // the default neutral panel. Replaces the per-dialog `background: white !important`
    // override that many dialogs carry.
    whiteContent: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    dialogDynamicClass() {
      return [
        'base-dialog',
        `base-dialog__${this.contentClass}`,
        this.sidePanel ? 'side-panel' : '',
        this.centered ? 'is-centered' : '',
        this.whiteContent ? 'has-white-content' : '',
      ]
        .filter(Boolean)
        .join(' ');
    },
  },
  data() {
    return {
      isModalOpen: false,
      // Stable, unique id linking the dialog to its title via aria-labelledby.
      titleId: `base-dialog-title-${this._uid}`,
    };
  },
  watch: {
    // v-dialog closes itself on Esc/outside-click without going through closeModal.
    isModalOpen(isOpen) {
      if (isOpen) {
        this.releaseBack = closeOnBack(() => this.dismiss());
        // Once its content is there (v-dialog 2.1 has no after-enter to listen to), and after
        // v-dialog has focused it on the next tick, so a listener can move the focus inside.
        setTimeout(() => this.$emit('opened'));
      } else {
        this.releaseBackButton();
        this.$emit('dialog-closed');
      }
    },
  },
  beforeDestroy() {
    this.releaseBackButton();
  },
  methods: {
    closeModal() {
      this.$emit('closeModal');
    },
    openModal() {
      this.isModalOpen = true;
    },
    // Back on a phone: as the dialog's ✕, or, where nothing listens to it, as its own Cancel.
    dismiss() {
      if (this.$listeners.closeModal) this.closeModal();
      else this.isModalOpen = false;
    },
    releaseBackButton() {
      if (this.releaseBack) this.releaseBack();
      this.releaseBack = null;
    },
  },
};
</script>

<style lang="scss">
.base-dialog__surface {
  // Transparent wrapper: exposes role="dialog"/aria-modal/aria-labelledby to the
  // accessibility tree without affecting the flex layout of .base-dialog.
  display: contents;
}

.base-dialog {
  background-color: $color-neutral-pale;
  border-radius: $border-radius-root;
  box-shadow: -7px 0 10px 0 rgba(141, 149, 171, 0.41);
  display: flex;
  flex-direction: column;
  height: 100%;
  left: initial;
  margin-right: 0;
  max-height: 100% !important;
  position: absolute;
  right: 0;
  top: -1.5625rem;
  width: 28.75rem;

  &.side-panel {
    border-radius: $border-radius-root 0 0 $border-radius-root;
  }

  &__tender {
    width: 100%;
    max-width: $dialog-max-width;

    @media (min-width: 1980px) {
      max-width: $dialog-max-width-xl;
    }
  }

  .base-dialog__header {
    align-items: center;
    background-color: $color-white;
    display: flex;
    justify-content: space-between;
    height: 85px;
    gap: 4px;

    .header__title {
      color: $color-normal;
      font-family: $font-family-raleway;
      font-size: 20px;
      font-stretch: normal;
      font-style: normal;
      font-weight: 800;
      letter-spacing: normal;
      line-height: 1.36;
      margin: 1rem 1rem 1rem 1.5rem;
      width: 100%;
    }

    .header__close-icon {
      color: #bdc9d6;
      height: 29px;
      margin-right: 1.2rem;
      width: 29px;

      &:hover {
        cursor: pointer;
      }
    }

    .close-icon__container {
      border-radius: 50%;
      outline: none;

      &:focus-visible {
        box-shadow: 0 0 0 2px $color-accent;
      }
    }
  }

  .base-dialog__header + .base-dialog__content {
    padding-top: 2rem;
    background-color: $color-neutral-pale;
  }

  .base-dialog__content {
    background-color: $color-white;
    display: flex;
    flex-direction: column;
    height: 100%;
    flex: 1;
    position: relative;
    overflow: auto;
  }

  .base-dialog__actions {
    background-color: $color-white;
  }

  // Opt-in white content area (see the whiteContent prop). Replaces the
  // per-dialog `background: white !important` override that dialogs used to carry
  // to beat the neutral `.base-dialog__header + .base-dialog__content` default.
  // Keeps one shared !important instead of scattering it across every call site.
  &.has-white-content {
    .base-dialog__content,
    .base-dialog__header + .base-dialog__content {
      background-color: $color-white !important;
      padding: $space-xl;
    }
  }
}

// On phones no dialog is wider than the screen: side panels, fixed at 460–850px, fill it
// instead of running off its left edge. :where() keeps this below any max-width a dialog
// sets for itself, like the 90vw that keeps some centered dialogs off the screen edges.
@include screen-below('tablet') {
  :where(.base-dialog) {
    max-width: 100%;
  }

  // Over each dialog's own sizes: none keeps a minimum wider than the screen, nor the panels' lift
  // (top: -1.5625rem), which cut a centered dialog's header when it ran the screen's height.
  .v-dialog.base-dialog {
    top: 0;
    min-width: 0;
  }

  // A panel fills the screen, whatever its width on larger screens (the BI tender's 40vw would
  // leave it 150px), without the dialog's margin the lift made up for.
  .v-dialog.base-dialog:not(.is-centered) {
    width: 100%;
    margin: 0;
  }

  // A centered dialog, in the middle of the screen, 16px from its edges as the pages' content.
  // The panel's right edge (margin-right: 0) left it 24px from the left edge and touching the
  // right one.
  .v-dialog.base-dialog.is-centered {
    width: calc(100% - 32px);
    max-width: calc(100% - 32px);
    max-height: calc(100% - 32px) !important;
    margin: 16px;
  }
}
</style>
